import useAnalytics, { eventName } from '@/hooks/use-analytics';
import { styled } from '@mui/material';
import Button from '@mui/material/Button';
import type { ButtonProps } from '@mui/material/Button';

export const GenericButton = ({
  component,
  label,
  id,
  type = 'button',
  onClick,
  href,
  fullWidth = true,
  variant = 'contained',
  size = 'large',
  startIcon,
  color,
  form,
  fireAnalytics = true,
  loading = false,
}: {
  component?: 'button' | 'a';
  fullWidth?: boolean;
  label: string;
  id: string;
  onClick?: () => void;
  href?: string;
  size?: 'small' | 'medium' | 'large';
  type?: 'submit' | 'reset' | 'button';
  variant?: 'text' | 'outlined' | 'contained';
  startIcon?: React.ReactNode;
  color?: 'inherit' | 'primary' | 'secondary' | 'tertiary' | 'success' | 'error' | 'info' | 'warning' | 'text';
  form?: string;
  fireAnalytics?: boolean;
  loading?: boolean;
}) => {
  const { trackClick } = useAnalytics();

  return (
    <StyledButton
      onClick={() => {
        if (fireAnalytics) trackClick(eventName(id));
        if (onClick) onClick();
      }}
      type={type}
      href={href}
      fullWidth={fullWidth}
      variant={variant}
      role='button'
      size={size}
      startIcon={loading ? null : startIcon}
      color={color}
      form={form}
      component={component}
      loading={loading}
    >
      {loading ? '' : label}
    </StyledButton>
  );
};

const StyledButton = styled(Button)<ButtonProps>(({ color }) => {
  const validColors = ['primary', 'secondary', 'tertiary', 'success', 'error', 'info', 'warning'];
  const safeColor = validColors.includes(color as string) ? color : 'secondary';

  const outlinedBorderColor = {
    '&.MuiButton-outlined': {
      color: `${safeColor === 'error' ? 'var(--mui-palette-error-main)' : 'var(--mui-palette-text-main)'}`,
      borderColor: `var(--mui-palette-${safeColor}-main)`,
      borderWidth: '1px',
      '&:hover': {
        borderColor: `var(--mui-palette-${safeColor}-dark)`,
      },
      '& .MuiTouchRipple-root': {
        color: `var(--mui-palette-${safeColor}-main)`,
      },
    },
  };

  return {
    ...outlinedBorderColor,
  };
});
